import { IBlowDef, Segment } from './types';
import { seg } from './segments';
import { ParamReader } from './ParamReader';

export const resources: Record<number, IBlowDef> = {
    11: {
        name: 'HP Recovery',
        wikiDesc: 'Recovers or drains HP every 2 seconds. When draining, HP will drop no lower than ' +
            'MinHP once below LossHP.',
        describe({ params }) {
            const amount = params.nextNum();
            if (isNaN(amount)) {
                return null;
            }
            const verb = amount < 0 ? 'Loses' : 'Recovers';
            return [`${verb} `, seg.num(Math.abs(amount)), ' HP every 2s'];
        },
    },
    12: {
        name: 'HP Recovery %',
        wikiDesc: 'Recover HP% every 2 seconds.',
        describe({ params }) {
            const pct = params.nextNum();
            if (isNaN(pct)) {
                return null;
            }
            const verb = pct < 0 ? 'Loses' : 'Recovers';
            return [`${verb} `, seg.pct(Math.abs(pct)), ' HP every 2s'];
        },
    },
    13: {
        name: 'Mana Restore',
        wikiDesc: 'Recover Mana every 2 seconds.',
        describe({ params }) {
            const amount = params.nextNum();
            if (isNaN(amount)) {
                return null;
            }
            const verb = amount < 0 ? 'Loses' : 'Recovers';
            return [`${verb} `, seg.num(Math.abs(amount)), ' MP every 2s'];
        },
    },
    14: {
        name: 'Mana Restore %',
        wikiDesc: 'Recover Mana% every 2 seconds.',
        describe({ params }) {
            const pct = params.nextNum();
            if (isNaN(pct)) {
                return null;
            }
            const verb = pct < 0 ? 'Loses' : 'Recovers';
            return [`${verb} `, seg.pct(Math.abs(pct)), ' MP every 2s'];
        },
    },
    15: {
        name: 'HP Restore',
        wikiDesc: 'Recover HP.',
        describe({ params }) {
            const amount = params.nextNum();
            if (isNaN(amount)) {
                return null;
            }
            const verb = amount < 0 ? 'Depletes' : 'Heals';
            return [`${verb} `, seg.num(Math.abs(amount)), ' HP'];
        },
    },
    16: {
        name: 'HP Restore %',
        wikiDesc: "Recover HP%. Adding a trailing ';1' lets this reduce HP to 0 (normally it can't kill).",
        describe({ params }) {
            const f = params.numbers();
            const pct = f[0];
            if (isNaN(pct)) {
                return null;
            }
            const verb = pct < 0 ? 'Depletes' : 'Heals';
            const out: Segment[] = [`${verb} `, seg.pct(Math.abs(pct)), ' HP'];
            if (f[1] === 1) {
                out.push(', capable of reducing HP to 0');
            }
            return out;
        },
    },
    17: {
        name: 'MP Restore',
        wikiDesc: 'Recover MP.',
        describe({ params }) {
            const amount = params.nextNum();
            if (isNaN(amount)) {
                return null;
            }
            const verb = amount < 0 ? 'Depletes' : 'Heals';
            return [`${verb} `, seg.num(Math.abs(amount)), ' MP'];
        },
    },
    18: {
        name: 'MP Restore %',
        wikiDesc: 'Recover MP%.',
        describe({ params }) {
            const pct = params.nextNum();
            if (isNaN(pct)) {
                return null;
            }
            const verb = pct < 0 ? 'Depletes' : 'Heals';
            return [`${verb} `, seg.pct(Math.abs(pct)), ' MP'];
        },
    },
    55: {
        name: 'HP barrier',
        wikiDesc: 'Grants a damage-absorbing barrier. The 1- and 3-param forms absorb a flat HP ' +
            "amount; the 4/5-param form absorbs HP% (an integer, e.g. 50 for 50%) of the caster's " +
            "max HP (or the receiver's, if the 5th param is 1) plus flat HP, optionally with bonus " +
            'super armor.',
        describe({ params }) {
            const f = params.numbers();
            if (!f.length || isNaN(f[0])) {
                return null;
            }
            // Arities (engine: DnShieldBlow.cpp ctor + OnBegin):
            //   [FlatHP] and [FlatHP;ApplySA;SAAmount] — flat absorb only, no max-HP scaling.
            //   [HP%;FlatHP;ApplySA;SAAmount(;UseReceiverMaxHP)] — HP% of the CASTER's max HP by
            //   default; a 5th param of 1 switches it to the receiver's (holder's) max HP.
            // A 2-param string isn't a documented arity; the engine atof()s the whole string,
            // which reads the leading number — same as the 1-param flat shape below.
            let applySa = false;
            let saAmount = 0;
            let saAmountSpan: [number, number] | undefined;
            const out: Segment[] = ['Grants a damage-absorbing barrier with '];
            if (f.length >= 4) {
                const hpPct = f[0];
                const flatHp = f[1] || 0;
                applySa = f[2] === 1;
                saAmount = f[3] || 0;
                saAmountSpan = params.span(3) || undefined;
                const useReceiverHp = f[4] === 1;
                out.push(
                    seg.val(hpPct + '%', params.span(0) || undefined),
                    useReceiverHp ? " of the receiver's max HP" : " of the caster's max HP");
                if (flatHp) {
                    out.push(' plus ', seg.num(flatHp, params.span(1) || undefined), ' flat HP');
                }
            } else {
                if (f.length === 3) {
                    applySa = f[1] === 1;
                    saAmount = f[2] || 0;
                    saAmountSpan = params.span(2) || undefined;
                }
                out.push(seg.num(f[0], params.span(0) || undefined), ' HP');
            }
            if (applySa) {
                out.push(', granting ', seg.num(saAmount, saAmountSpan), ' super armor');
            }
            return out;
        },
    },
    91: {
        name: 'Absolute HP Attack',
        wikiDesc: 'Adjusts HP by amt.',
        describe({ params }) {
            const amt = params.nextNum();
            if (isNaN(amt)) {
                return null;
            }
            const verb = amt < 0 ? 'Deals' : 'Restores';
            return [`${verb} `, seg.num(Math.abs(amt)), ' HP (absolute)'];
        },
    },
    92: {
        name: 'Absolute HP% Attack',
        wikiDesc: 'Adjusts HP by amt%.',
        describe({ params }) {
            const amt = params.nextNum();
            if (isNaN(amt)) {
                return null;
            }
            const verb = amt < 0 ? 'Deals' : 'Restores';
            return [`${verb} `, seg.pct(Math.abs(amt)), ' HP (absolute)'];
        },
    },
    140: {
        name: 'Therapeutic',
        wikiDesc: "Restores the target's HP based on the caster's MATK: MATK * Ratio.",
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return ['Heals the target for ', seg.pct(ratio), " of the caster's MATK"];
        },
    },
    141: {
        name: 'Heal Increase',
        wikiDesc: 'Increases the amount of HP healed by skills or items by ratio%.',
        note: 'The parameter is a multiplier, e.g. 1.2 = +20% healing.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            const delta = ratio - 1;
            if (delta === 0) {
                return null;
            }
            const dir = delta > 0 ? 'increased' : 'decreased';
            return [`Incoming healing effects ${dir} by `, seg.pct(Math.abs(delta))];
        },
    },
    143: {
        name: 'Lifesaver',
        wikiDesc: 'HP is unable to drop below 1 for SecondsOfUndying. Seems to be bugged on monsters ' +
            'above a certain HP amount and prematurely stops damage.',
        describe({ params }) {
            const secs = params.nextNum();
            if (isNaN(secs)) {
                return null;
            }
            return ['HP cannot drop below 1 for ', seg.secs(secs * 1000)];
        },
    },
    173: {
        name: 'Ice Barrier',
        wikiDesc: "Creates a shield with durability = the caster's max MATK * Rate; absorbed hits " +
            'cause no hit reaction. When the shield ends (expiry or durability emptied), enemies within ' +
            'Radius are frozen (blow 41, using FreezeValue as its param) for FreezeTime.',
        describe({ params }) {
            const g = params.groups();
            if (!g.groups.length) {
                return null;
            }
            const settings = new ParamReader(g.groups[0]).numbers();
            const rate = settings[0];
            const radius = settings[1];
            const freezeMs = settings[2];
            if (isNaN(rate) || isNaN(radius) || isNaN(freezeMs)) {
                return null;
            }
            const out: Segment[] = [
                'Creates a shield with durability equal to ', seg.pct(rate),
                " of the caster's max MATK (absorbed hits cause no hit reaction). When the shield ends, " +
                'enemies within ', seg.num(radius), ' range are frozen for ', seg.secs(freezeMs),
            ];
            if (g.groups.length > 1) {
                const freezeValue = ParamReader.num(g.groups[1]);
                if (!isNaN(freezeValue)) {
                    out.push(': ', seg.child(41, String(freezeValue), freezeMs));
                }
            }
            return out;
        },
    },
    223: {
        name: 'MP Burst',
        wikiDesc: "On apply, deals Ratio * current MP as damage to the holder and drains that much MP " +
            '(can kill). Only fires if the current MP fraction is above MinMPRatio.',
        describe({ params }) {
            const f = params.numbers();
            const ratio = f[0];
            if (isNaN(ratio)) {
                return null;
            }
            const out: Segment[] = [
                'On apply, deals ', seg.pct(ratio),
                ' of current MP as damage and drains that MP (can kill)',
            ];
            const minMpRatio = f[1];
            if (!isNaN(minMpRatio)) {
                out.push(', only if current MP is above ', seg.pct(minMpRatio));
            }
            return out;
        },
    },
    227: {
        name: 'Lifesteal',
        wikiDesc: "Applied to the victim by a hit; heals the skill user for Ratio of that hit's " +
            'damage (capped at max HP). Counts as healing, so healing reduction (405) suppresses it.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return [
                'Heals the skill user for ', seg.pct(ratio),
                " of the hit's damage dealt (capped at max HP; counts as healing, so healing reduction " +
                '(', seg.blow(405), ') suppresses it)',
            ];
        },
    },
    231: {
        name: 'Continuous Base MP Regen',
        wikiDesc: 'Every 2s (fixed), changes MP by Value in base MP units (scaled by job/level weights ' +
            'from playerleveltable); negative drains.',
        describe({ params }) {
            const f = params.numbers();
            const value = f[0];
            if (isNaN(value)) {
                return null;
            }
            const verb = value < 0 ? 'Drains' : 'Restores';
            const out: Segment[] = [
                `${verb} `, seg.num(Math.abs(value)), ' base MP every 2s (scaled by job/level weights)',
            ];
            if (f[1] === 1) {
                out.push(' (shown as a floating number)');
            }
            return out;
        },
    },
    243: {
        name: 'Mana Shield',
        wikiDesc: 'When damage is received, a rate% of the damage will be consumed from MP instead of HP.',
        describe({ params }) {
            const f = params.numbers();
            const rate = f[0];
            if (isNaN(rate)) {
                return null;
            }
            const out: Segment[] = [
                'When damage is received, ', seg.pct(rate), ' of it is consumed from MP instead of HP',
            ];
            const mpReductionDecrease = f[1];
            if (mpReductionDecrease) {
                out.push(', with the MP-consumption rate decreasing by ', seg.pct(mpReductionDecrease));
            }
            return out;
        },
    },
    248: {
        name: 'Recovery on Attack',
        wikiDesc: 'Recovers a % of the damage dealt by a hit as HP. Can be capped to a flat HP amount ' +
            'for the buff lifetime, a % of max HP for the buff lifetime, or a % of max HP per hit.',
        note: "If using '% Max HP Limit Total Recovery', 'Flat Limit Total Recovery' MUST be -1.",
        describe({ params }) {
            const f = params.numbers();
            const pctRecovered = f[0];
            if (isNaN(pctRecovered)) {
                return null;
            }
            const out: Segment[] = ['Recovers ', seg.pct(pctRecovered), ' of damage dealt as HP'];
            const flatLimitTotal = f[1];
            const pctMaxHpLimitTotal = f[2];
            const pctMaxHpLimitPerHit = f[3];
            const requiredSE = f[4];
            const caps: Segment[][] = [];
            if (!isNaN(flatLimitTotal) && flatLimitTotal >= 0) {
                caps.push([seg.num(flatLimitTotal), ' flat, for the lifetime of the buff']);
            }
            if (pctMaxHpLimitTotal) {
                caps.push([seg.pct(pctMaxHpLimitTotal), ' of max HP, for the lifetime of the buff']);
            }
            if (pctMaxHpLimitPerHit) {
                caps.push([seg.pct(pctMaxHpLimitPerHit), ' of max HP, per hit']);
            }
            if (caps.length) {
                out.push(', capped at ', seg.list(caps));
            }
            if (requiredSE) {
                out.push(', requiring ', seg.blow(requiredSE));
            }
            return out;
        },
    },
    275: {
        name: 'Disallow healing',
        wikiDesc: 'Disallows healing except from the listed skill IDs. With no IDs given, disallows ' +
            'healing from all sources.',
        describe({ params }) {
            const ids = params.numbers().filter((v) => !isNaN(v));
            if (!ids.length) {
                return ['Disallows healing from all sources'];
            }
            return [
                'Disallows healing except from ',
                seg.list(ids.map((id) => [seg.skill(id)] as Segment[])),
            ];
        },
    },
    315: {
        name: 'Heal Over Time from %',
        wikiDesc: 'Applies a heal every IntervalMS based on Mod% of the given stat. AtkType 0 = PATK, ' +
            '1 = MATK, 2 = MaxHP.',
        describe({ params }) {
            const f = params.numbers();
            const atkType = f[0];
            const ratio = f[1];
            const flatHeal = f[2] || 0;
            const tickDelayMs = f[4];
            if (isNaN(atkType) || isNaN(ratio) || isNaN(tickDelayMs)) {
                return null;
            }
            const ATK_TYPES = ['PATK', 'MATK', 'MaxHP'];
            const out: Segment[] = [
                'Every ', seg.secs(tickDelayMs), ', heals ', seg.pct(ratio), ' of ',
                seg.en(atkType, ATK_TYPES, 'unknown'),
            ];
            if (flatHeal) {
                out.push(' plus ', seg.num(flatHeal), ' flat');
            }
            return out;
        },
    },
    405: {
        name: 'Healing Ratio Change',
        wikiDesc: 'Multiplies incoming healing on the target by Ratio; stacks additively on ' +
            '(Ratio - 1), and a negative result turns heals into HP loss. SkillType 1 = only modifies ' +
            'active-skill heals AND fully blocks potion/passive/regen heals while up.',
        describe({ params }) {
            const f = params.numbers();
            const ratio = f[0];
            if (isNaN(ratio)) {
                return null;
            }
            const delta = ratio - 1;
            const dir = delta < 0 ? 'decreased' : 'increased';
            const out: Segment[] = [
                `Incoming healing ${dir} by `, seg.pct(Math.abs(delta)),
                ' (stacks additively; a negative result turns heals into HP loss)',
            ];
            if (f[1] === 1) {
                out.push(
                    ' — only modifies active-skill heals, and fully blocks potion/passive/regen ' +
                    'heals while up');
            }
            return out;
        },
    },
    411: {
        name: 'Hero Gauge Change',
        wikiDesc: 'Every Interval ms, grants Point + MaxGauge×Rate hero skill gauge points (negative ' +
            'Point drains). Interval 0 = apply once immediately then end. Players only.',
        describe({ params }) {
            const f = params.numbers();
            const intervalMs = f[0];
            const point = f[1];
            if (isNaN(intervalMs) || isNaN(point)) {
                return null;
            }
            const out: Segment[] = [];
            if (intervalMs > 0) {
                out.push('Every ', seg.secs(intervalMs), ', grants ');
            } else {
                out.push('Grants ');
            }
            out.push(seg.num(point), ' hero gauge');
            const rate = f[2];
            if (rate) {
                out.push(' plus ', seg.pct(rate), ' of max gauge');
            }
            if (intervalMs <= 0) {
                out.push(' once, immediately, then ends');
            }
            out.push(' (players only)');
            return out;
        },
    },
    412: {
        name: 'Increase Hero Gauge Gain Rate',
        wikiDesc: 'Multiplies gains to hero gauge; does not affect decreases.',
        describe({ params }) {
            const multiplier = params.nextNum();
            if (isNaN(multiplier)) {
                return null;
            }
            return [
                'Multiplies hero gauge gains by ', seg.pct(multiplier), ' (does not affect decreases)',
            ];
        },
    },
    413: {
        name: 'Cannot Gain Hero Gauge',
        wikiDesc: 'Positive hero gauge changes are ignored while applied; decreases still go through.',
        describe() {
            return ['Positive hero gauge changes are ignored while applied (decreases still go through)'];
        },
    },
};
